import type { LexicalNode } from 'lexical';
import {
    $createLineBreakNode,
    $createParagraphNode,
    $createTextNode,
    $getRoot,
    $isElementNode,
    $isLineBreakNode,
} from 'lexical';

import { REFERENCE_KINDS } from './referenceKinds';
import type { Reference, ReferenceKind } from './ReferenceMentionNode';
import { $createReferenceMentionNode, $isReferenceMentionNode } from './ReferenceMentionNode';

// Wire format `@[kind:id|Name]`: the id is what the agent acts on, the name
// keeps the message readable without passing the reference as extra context.
// Built from REFERENCE_KINDS so adding a kind can't leave the parser behind.
const MENTION_TOKEN_REGEX = new RegExp(
    String.raw`@\[(${REFERENCE_KINDS.join('|')}):([^\]|]+)\|([^\]]*)\]`,
    'g',
);

export function formatMentionToken(reference: Reference): string {
    return `@[${reference.kind}:${reference.id}|${reference.name}]`;
}

export function containsMentionToken(message: string): boolean {
    // `test` advances `lastIndex` on a /g regex, so reset it first.
    MENTION_TOKEN_REGEX.lastIndex = 0;
    return MENTION_TOKEN_REGEX.test(message);
}

type RawSegment =
    | { type: 'text'; value: string }
    | { type: 'mention'; kind: ReferenceKind; id: string; name: string };

export function splitOnMentionTokens(message: string): RawSegment[] {
    const segments: RawSegment[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    MENTION_TOKEN_REGEX.lastIndex = 0;
    while ((match = MENTION_TOKEN_REGEX.exec(message)) !== null) {
        const [, kind, id, name] = match;
        if (match.index > lastIndex) {
            segments.push({ type: 'text', value: message.slice(lastIndex, match.index) });
        }
        segments.push({ type: 'mention', kind: kind as ReferenceKind, id, name });
        lastIndex = MENTION_TOKEN_REGEX.lastIndex;
    }

    if (lastIndex < message.length) {
        segments.push({ type: 'text', value: message.slice(lastIndex) });
    }

    return segments;
}

/** Must be called inside an `editor.update()` callback. */
export function $populateEditorFromMessage(message: string): void {
    const root = $getRoot();
    root.clear();

    const paragraph = $createParagraphNode();

    const appendText = (value: string) => {
        const lines = value.split('\n');
        lines.forEach((line, index) => {
            if (index > 0) {
                paragraph.append($createLineBreakNode());
            }
            if (line) {
                paragraph.append($createTextNode(line));
            }
        });
    };

    for (const segment of splitOnMentionTokens(message)) {
        if (segment.type === 'mention') {
            paragraph.append(
                $createReferenceMentionNode({
                    kind: segment.kind,
                    id: segment.id,
                    name: segment.name,
                }),
            );
        } else {
            appendText(segment.value);
        }
    }

    root.append(paragraph);
}

function nodeToMessageString(node: LexicalNode): string {
    if ($isReferenceMentionNode(node)) {
        return formatMentionToken({
            kind: node.getReferenceKind(),
            id: node.getReferenceId(),
            name: node.getReferenceName(),
        });
    }
    if ($isLineBreakNode(node)) {
        return '\n';
    }
    if ($isElementNode(node)) {
        return node.getChildren().map(nodeToMessageString).join('');
    }
    return node.getTextContent();
}

/** Must be called inside an `editor.read()` or `editor.update()` callback. */
export function $serializeEditorToMessage(): string {
    return $getRoot().getChildren().map(nodeToMessageString).join('\n');
}
